import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material"; // Ensure this path is correct
import type { ProgressRevaluationRequestResp } from "adl-gen/ferovinum/app/api";
import type { RevaluationRequestState } from "adl-gen/ferovinum/app/db";
import type { DealTermsView } from "adl-gen/ferovinum/app/views";
import { useState } from "react";
import { useConfirmationDialog } from "../../context/global-dialog/use-dialog";
import { LoadingActionButton } from "../../widgets/buttons/loading-action-button/loading-action-button";
import { TermsLink } from "../../widgets/link/terms-link";

export interface AcceptTermsComponentProps {
  currentState: RevaluationRequestState;
  dealTerms: DealTermsView;
  acknowledgeDealTerms: (
    accept: boolean,
  ) => Promise<ProgressRevaluationRequestResp | undefined>;
}

export const AcceptTermsComponent = ({
  currentState,
  dealTerms,
  acknowledgeDealTerms,
}: AcceptTermsComponentProps) => {
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showConfirmationDialog } = useConfirmationDialog();
  const isActionable = currentState.toString() === "created";

  const handleReject = async () => {
    await showConfirmationDialog({
      title: "Are you sure you want to reject these terms?",
      confirmAction: {
        color: "error",
        title: "Reject",
        onClick: async () => {
          await acknowledgeDealTerms(false);
        },
      },
    });
  };

  const handleAccept = async () => {
    setLoading(true);
    await acknowledgeDealTerms(true);
    await setLoading(false);
  };

  if (!isActionable) {
    return null;
  }

  return (
    <Stack
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      sx={{
        p: 2,
        backgroundColor: "common.grey3",
      }}
    >
      <Stack spacing={3} direction="row" justifyContent="space-between">
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                value={acceptTerms}
                onClick={() => setAcceptTerms(!acceptTerms)}
                disabled={loading}
              />
            }
            label={
              <Stack direction="row" spacing={1}>
                <Typography>I agree to the </Typography>
                <TermsLink dealTermsView={dealTerms} />
              </Stack>
            }
          />
        </FormGroup>
        <Button
          variant="outlined"
          color="error"
          disabled={loading}
          onClick={handleReject}
        >
          Reject
        </Button>
        <LoadingActionButton
          variant="contained"
          color="success"
          disabled={!acceptTerms || loading}
          onClick={handleAccept}
        >
          Accept
        </LoadingActionButton>
      </Stack>
    </Stack>
  );
};
